import { sort } from "@/services/object.js";
import { openAPIFactory } from "@/services/open-api.js";
import { NutzerRestRechte } from "@/services/open-api/models/nutzer-rest.ts";
import { useDocumentsStore } from "@/stores/documents.ts";
import { useFundamentalStore } from "@/stores/fundamental.ts";
import { useProceedingsStore } from "@/stores/proceedings.ts";

/**
 * Download File
 * @param {string} fileID
 * @param {string} planID
 * @returns {AxiosPromise<string[]>}
 */
export function downloadFile(fileID, planID) {
  return openAPIFactory
    .dokumentResourceApiFactory()
    .getDokumentDatei(planID, fileID, undefined, undefined, {
      responseType: "blob",
    });
}

/**
 * Download Planarchiv
 * @param {string} planID
 * @param {string} xPlanBoxID the ID of the planarchiv in the XPlanBox
 * @returns {AxiosPromise<string[]>}
 */
export function downloadPlanarchive(planID, xPlanBoxID) {
  return openAPIFactory.verfahrenResourceApiFactory().getXPlanManagerRessource(planID, xPlanBoxID, {
    responseType: "blob",
  });
}

/**
 *  Upload Document templates
 *  @param {File} vorlage
 *  @param {string} codeMusterdokument
 *  @param {string} codeUnterlagentyp
 *  @param skipValidation
 *  @returns {AxiosPromise<VorlageRest>}
 * */
export async function uploadDocumentTemplate(
  vorlage,
  codeMusterdokument,
  codeUnterlagentyp,
  skipValidation,
) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .update(vorlage, codeMusterdokument, codeUnterlagentyp, skipValidation)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 *  Create New Document templates
 *  @param {object} payload
 *  @param {File} payload.vorlage
 *  @param {string} payload.codeMusterdokument
 *  @param {string} payload.codeVerfahrensunterlagetyp
 *  @param {string} payload.beschreibung
 *  @param {boolean} payload.skipValidation - optional
 *  @returns {AxiosPromise<VorlageRest>}
 */
export async function createNewDocumentTemplate({
  vorlage,
  codeMusterdokument,
  codeVerfahrensunterlagetyp,
  beschreibung,
  skipValidation,
}) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .create(vorlage, codeMusterdokument, codeVerfahrensunterlagetyp, beschreibung, skipValidation)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Validate Document Template by Code Mustervorlage
 * @returns {AxiosPromise<string[]>}
 * @param {string} code - Code Mustervorlage
 */
export function validateDocumentTemplate(code) {
  return new Promise((resolve, reject) => {
    return openAPIFactory
      .vorlageResourceApiFactory()
      .pruefeVorlage1(code)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Validate temporaray uploaded document template
 * @returns {AxiosPromise<string[]>}
 * @param {File} file
 * @param {string} code
 * @param {string} beschreibung
 */
export function validateTemporaryUploadedDocumentTemplate(file, code, beschreibung) {
  return new Promise((resolve, reject) => {
    return openAPIFactory
      .vorlageResourceApiFactory()
      .pruefeVorlage(file, code, beschreibung)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Download Document Template
 * @param {string} codeMusterDokument
 * @param {string} version -optional
 * @returns {AxiosPromise<string[]>}
 */
export function downloadDocumentTemplate(codeMusterDokument, version = "") {
  return openAPIFactory.vorlageResourceApiFactory().getVorlageDatei(codeMusterDokument, version, {
    responseType: "blob",
  });
}

/**
 * create document from template
 * @param {object} payload
 * @param {string} payload.planID
 * @param {string} payload.codeMusterdokument
 * @param {string} payload.codeVerfahrensteilschritte
 * @return {Promise<DokumentRest>}
 */
export async function createDocumentFromTemplateWithCodeVts({
  planID,
  codeMusterdokument,
  codeVerfahrensteilschritte,
}) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .erzeugeDokumentDateiAusVorlage1(planID, codeMusterdokument, codeVerfahrensteilschritte)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Set proceeding steps for Document template
 * @param {object} payload
 * @param {VorlageRest} payload.vorlageRest REST-Object with new proceeding steps and substeps
 * @param {string} payload.codeMusterdokument
 * @returns {Promise<VorlageRest>}
 */
export async function setProceedingStepsAndSubSteps({ vorlageRest, codeMusterdokument }) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .setVorlageVsVts(vorlageRest, codeMusterdokument)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Gets the next free codeMusterDokument
 * @returns {AxiosPromise<string>}
 */
export async function getCodeMusterDokument() {
  return openAPIFactory.vorlageResourceApiFactory().determineNextFreeCodeMusterdokument();
}

/**
 *  Delete Document template
 *  @param {string} codeMusterdokument
 *  @returns {Promise<void>}
 * */
export async function deleteDocumentTemplate(codeMusterdokument) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .deleteVorlage(codeMusterdokument)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Publish the document
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 * @throws {RequiredError}
 */
export async function publishDocument(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .publishDokument(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Unpublish the document
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 * @throws {RequiredError}
 */
export async function unPublishDocument(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .unpublishDokument(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Updates the vormerkungen for a document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} newVormerkungen Array of codeVormerkungen to update to
 * @param {CodeVormerkung[]} oldVormerkungen Array of current codeVormerkungen
 * @returns {Promise<void>}
 */
export function updateDocumentVormerkungen(planID, fileID, newVormerkungen, oldVormerkungen) {
  return new Promise((resolve, reject) => {
    const vormerkungenToAdd = newVormerkungen.filter(
      (v) => !oldVormerkungen.some((e) => e.code === v.code),
    );
    const vormerkungenToDelete = oldVormerkungen.filter(
      (v) => !newVormerkungen.some((e) => e.code === v.code),
    );
    let promisesArr = [];

    if (vormerkungenToAdd.length) {
      promisesArr.push(setDocumentVormerkungen(planID, fileID, vormerkungenToAdd));
    }

    if (vormerkungenToDelete.length) {
      promisesArr.push(deleteDocumentVormerkungen(planID, fileID, vormerkungenToDelete));
    }

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Sets vormerkungen to the document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} vormerkungen Array of codeVormerkungen to add
 * @returns {Promise<void>}
 */
export function setDocumentVormerkungen(planID, fileID, vormerkungen) {
  return new Promise((resolve, reject) => {
    let promisesArr = [];

    vormerkungen.forEach((vormerkung) => {
      const dokumentVormerkungRest = { fileIDs: [fileID], codeVormerkung: vormerkung };

      promisesArr.push(
        openAPIFactory
          .dokumentResourceApiFactory()
          .setDokumentVormerkung(dokumentVormerkungRest, planID),
      );
    });

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Removes vormerkungen from the document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} vormerkungen Array of codeVormerkungen to delete
 * @returns {Promise<void>}
 */
export function deleteDocumentVormerkungen(planID, fileID, vormerkungen) {
  return new Promise((resolve, reject) => {
    let promisesArr = [];

    vormerkungen.forEach((vormerkung) => {
      promisesArr.push(
        openAPIFactory
          .dokumentResourceApiFactory()
          .loescheDokumentVormerkung(planID, fileID, vormerkung.code),
      );
    });

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Sorts documents asc/desc
 * @param {object[]} mappedDocumentData document data to sort
 * @param {string} category category name to sort by
 * @param {boolean} asc true if asc false if desc
 * @return {object[]} sorted documents
 */
export function getSortedDocuments(mappedDocumentData, category, asc) {
  let dataType = undefined;
  let propertyName = undefined;

  if (category === "vertragsart") {
    dataType = "object";
    propertyName = "name";
  }

  if (category === "lastUpdate") {
    dataType = "date";
  }

  if (category === "status") {
    dataType = "object";
    propertyName = "name";
  }

  if (category === "vormerkungen") {
    return getSortedVormerkungen(mappedDocumentData, asc);
  }

  return sort(mappedDocumentData, category, asc, dataType, propertyName);
}

/**
 * Helper function to sort document vormerkungen
 * @param {object[]} mappedDocumentData documents to sort
 * @param {boolean} asc true if asc false if desc
 * @returns {object[]}
 */
function getSortedVormerkungen(mappedDocumentData, asc) {
  return mappedDocumentData.sort((a, b) => {
    let nameA, nameB;

    if (asc) {
      nameA = a["vormerkungen"].length ? a["vormerkungen"][0].name.toLowerCase() : undefined;
      nameB = b["vormerkungen"].length ? b["vormerkungen"][0].name.toLowerCase() : undefined;
    } else {
      nameA = b["vormerkungen"].length ? b["vormerkungen"][0].name.toLowerCase() : undefined;
      nameB = a["vormerkungen"].length ? a["vormerkungen"][0].name.toLowerCase() : undefined;
    }

    if (nameA === undefined) return 1;
    if (nameB === undefined) return -1;

    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;

    return 0;
  });
}

/**
 * Checks if document is currently published
 * @param {DokumentstatusRest[]} documentStatus
 * @returns {boolean}
 */
export function canBeDeleted(documentStatus) {
  return documentStatus.at(-1)?.dokumentstatus?.code !== "2100";
}

/**
 * Gets vormerkung names from vormerkungen array and sorts alphabetically
 * @param {VormerkungRest[]} vormerkungenArray
 * @returns {CodeVormerkung[]} sorted vormerkungen names
 */
export function getVormerkungen(vormerkungenArray) {
  const vormerkungen = vormerkungenArray.map((vormerkung) => vormerkung.vormerkung);

  return sort(vormerkungen, "name", true);
}

/**
 * Upload a new document
 * @param {object} documentInfo DokumentNeuRest
 * @param {File} file
 * @param {string} planID
 * @returns {Promise<DokumentRest>}
 */
export function uploadDocument(documentInfo, file, planID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .uploadDokument(documentInfo, file, planID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Get documents sorted by sections *
 * @param proceedingStructures - folder structure for documents of proceeding phases
 * @param foldersStructure - folder structure for documents of "fachliche Ordner"
 * @returns {Object} -  structure of section
 * e.g. {"0000Uebergreifend": {name: string}, "0002Test": {name: "Titel B", ...}, 2000: {name: "Titel A", ...}}
 * */
export function getDocumentSectionStructure(proceedingStructures, foldersStructure) {
  /**
   *  oben werden die Übergreifenden Dokumente UND Verträge angezeigt
   *  dann kommen alle fachlichen Ordner in alphabetischer Reihenfolge
   *  hinterher die Verfahrensschritte in der korrekten Reihenfolge, wie sie auch im Verfahren stattfinden
   *
   *  0000Uebergreifend   beinhaltet die übergreifenden Dokumente
   *  0001Vertraege       beinhaltet die Vertragsdokumente
   *  0002 + foldername   beinhaltet die jeweiligen Dokumente für den einzelnen fachlichen Ordner (Umweltordner, Auslegungsordner, ...)
   *  1000 ff             sind die Codes der Verfahrensschritte
   */
  return Object.assign(
    {},
    { "0000Uebergreifend": { name: "Übergreifende Dokumente" } },
    proceedingStructures,
    foldersStructure,
  );
}
/**
 * Sort section structure and return list of sorted IDs
 * Usecase: sorting of an object is browser-dependent, therefor we need an array
 * @param sectionStructure {Object} - structure of section e.g. {0002: {name: "Titel B"}, 0001: {name: "Titel A"}}
 * @returns {String[]} - ordered section IDs e.g. ["0001", "0002"]
 */
export function getDocumentSectionStructureOrder(sectionStructure) {
  const userIsOrganization = useFundamentalStore().checkUserPermission(
    NutzerRestRechte.EXTERNES_PLANUNGSBUERO,
  );
  const headerSections = [],
    phaseSections = [];

  Object.keys(sectionStructure).forEach((index) => {
    if (userIsOrganization && index === "0001Vertraege") {
      return;
    } else if (index.startsWith("000")) {
      headerSections.push(index);
    } else {
      phaseSections.push(index);
    }
  });

  headerSections.sort((a, b) => (a > b ? 1 : -1));
  phaseSections.sort((a, b) => (a > b ? 1 : -1));

  return headerSections.concat(phaseSections);
}

/**
 * Retrieve the alphabetical order of all "fachliche Ordner" for this proceeding
 * @param {object[]} documents - array of documents
 * @returns {object} - folder structure for "fachliche Ordner"
 * e.g. {"0001Vertraege": {name: "Verträge"}, "0002Test": {name: "Test"}}
 */
export function getFoldersStructure(documents) {
  const sortedStructure = documents
    .map((doc) => doc.ordnername)
    .filter((doc) => doc)
    .sort((a, b) => (a > b ? 1 : -1));

  let returnedStructure = {};

  sortedStructure.forEach((folder) => {
    if (folder === "Verträge") {
      // Verträge Folder should be always on top
      returnedStructure["0001Vertraege"] = { name: folder };
    } else {
      returnedStructure["0002" + folder] = { name: folder };
    }
  });

  // always show Verträge Folder
  if (!returnedStructure["0001Vertraege"]) {
    returnedStructure["0001Vertraege"] = { name: "Verträge" };
  }

  return returnedStructure;
}

/**
 * Group documents in sections by VS, technical folder and superior documents
 * @param {DokumentRest[]} documents - array of documents
 * @param {string} proceedingType - code of proceeding type
 * @param {CombinedProceedingStructure} proceedingStructures - object containing the proceeding steps
 * @param folderStructures - folder structure for "fachliche Ordner"
 * @returns {object} - object containing grouped documents
 * */
export function groupDocumentsInSection(
  documents,
  proceedingType,
  proceedingStructures,
  folderStructures = undefined,
) {
  const groupedFiles = { "0000Uebergreifend": [] };

  const foldersStructure = folderStructures ?? getFoldersStructure(documents);

  // get all folders ("fachliche ordner") for documents
  Object.keys(foldersStructure).forEach((index) => {
    groupedFiles[index] = [];
  });

  // get all phases ("verfahrensschritte") of this proceeding configuration
  Object.keys(proceedingStructures).forEach((phaseCode) => {
    groupedFiles[phaseCode] = [];
  });

  // sort documents into groupedFiles
  documents.forEach((document) => {
    if (!document.ordnername) {
      if (!document.verfahrensteilschritt) {
        // sort document into superior folder
        groupedFiles["0000Uebergreifend"].push(document);
      } else {
        // sort document into corresponding phase ("verfahrensschritt")
        const phaseCode = Object.keys(proceedingStructures).find(
          (code) =>
            proceedingStructures[code][proceedingType]?.[document.verfahrensteilschritt.code],
        );

        if (phaseCode) {
          groupedFiles[phaseCode].push(document);
        } else {
          groupedFiles["0000Uebergreifend"].push(document);
        }
      }
    } else {
      // sort document into corresponding folder ("fachlicher ordner")
      const folderKey = Object.keys(foldersStructure).find(
        (key) => foldersStructure[key].name === document.ordnername,
      );

      if (folderKey) {
        groupedFiles[folderKey].push(document);
      }
    }
  });

  return groupedFiles;
}

/**
 * Update a document
 * @param {File} file
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 */
export function updateDocument(file, planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .updateDokumentVersion(file, planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Returns the file information of a specific file
 * @param {string} fileID The fileID of the file
 * @param {string} planID The planID of the proceeding
 * @returns {DokumentRest} The info
 */
export function getFileInformation(fileID, planID) {
  const documentsStore = useDocumentsStore();
  const proceedingDocuments = documentsStore.getAllDocumentsByProceedingID(planID);

  return proceedingDocuments.filter((file) => file.fileID === fileID).shift();
}

/**
 * Converts the specified file size from bytes into the largest unit possible, e.g. MB
 * @param {number} fileSize The file size in bytes
 * @returns {string} The converted file size
 */
export function getFileSize(fileSize) {
  const unit = [" bytes", " kB", " MB", " GB"];
  let step = 0;
  let output = fileSize;

  while (output > 1024 && typeof unit[step + 1] !== "undefined") {
    step++;
    output /= 1024;
  }

  return Math.round(output * 100) / 100 + unit[step];
}

/**
 *
 * @param {string} filename
 * @param {string} planID
 * @param {string} activeVS
 * @returns {Promise<boolean>}
 */
export async function checkIfDocumentNameAlreadyExists(filename, planID, activeVS) {
  const documentsStore = useDocumentsStore();
  const proceedingsStore = useProceedingsStore();

  await documentsStore.loadProceedingDocuments({
    proceedingID: planID,
  });

  const proceedingWorkingCopy = proceedingsStore.currentProceedingWorkingCopy;
  const proceedingType = proceedingWorkingCopy.verfahrenssteuerung.code;
  const proceedingsStructures = proceedingsStore.proceedingStructuresByProceedingID(planID);
  const documents = documentsStore.getDocumentsByProceedingID(
    planID,
    proceedingType,
    proceedingsStructures,
  );
  const groupedDocuments = groupDocumentsInSection(
    documents,
    proceedingType,
    proceedingsStructures,
  );

  return groupedDocuments[activeVS === "0000" ? "0000Uebergreifend" : activeVS]?.find(
    (el) => el.filename === filename,
  );
}

/**
 * Get Externe Dienst Urls
 * @param {string} planID
 * @returns {Promise<ExterneUrlsRest>}
 * @throws {RequiredError}
 */
export async function getXplanExterneUrls(planID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getAllXplanExterneUrls(planID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Loads details of a specific document file.
 * @param {string} fileID The document's ID.
 * @param {string} planID
 * @returns {Promise<DokumentRest>}
 */
export function getDocumentDetails(fileID, planID) {
  return new Promise((resolve, reject) => {
    if (planID === undefined) {
      resolve(false);
    }

    openAPIFactory
      .dokumentResourceApiFactory()
      .getDokumentDetail(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Gets all XPlanGMLs.
 */
export function getAllXPlanGML() {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getAllXplanGml()
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Checks if Verfahrensstand matches the Rechtsstand of the provided file.
 * @param {String} planID
 * @param {String} fileID
 * @returns {Promise<RechtsstandValidationRest>}
 */
export function getRechtsstandValidation(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getRechtsstandValidation(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Restores a particular version of  a document.
 * @param {String} planID
 * @param {String} fileID
 * @param {string} version
 * @param {string} comment
 * @returns {Promise<RechtsstandValidationRest>}
 */
export function restoreDocumentVersion(planID, fileID, version, comment) {
  const restoreRequestBody = {
    kommentar: comment,
  };

  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .versionWiederherstellen(restoreRequestBody, planID, fileID, version)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Returns the object with current version of the document
 * @param {Array} versions
 * @returns {Object}
 */
export function getCurrentVersion(versions) {
  return versions.reduce((maxValue, obj) => {
    const parts1 = obj.versionsNummer.split(".").map(Number);
    const parts2 = maxValue.versionsNummer.split(".").map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return obj;
      if (num1 < num2) return maxValue;
    }
    return maxValue;
  }, versions[0]);
}

/**
 * Returns the filename from a Content-Disposition header
 * @param {string} header
 * @param {string} extension
 * @return {string}
 * @example
 * const header = "attachment; filename*=UTF-8''example%20file.pdf";
 * "example file.pdf"
 * @example
 * const header = "attachment; filename=\"document.pdf\"";
 * "document.pdf"
 */
export function getFilenameFromContentDisposition(header, extension) {
  let filename = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);

  if (filename) {
    return decodeURIComponent(filename[1]);
  }

  filename = header.match(/filename\s*=\s*"([^"]+)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  if (filename) {
    return filename[1].trim();
  }

  return extension ? `download.${extension}` : "download";
}

export const mimeToExtension = {
  "application/pdf": "pdf",
  "application/json": "json",
  "text/csv": "csv",
  "application/xml": "xml",
  "image/png": "png",
  "image/jpeg": "jpg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": "dotx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/zip": "zip",
};

export const extensionToMime = Object.fromEntries(
  Object.entries(mimeToExtension).map(([mime, ext]) => [ext, mime]),
);

/**
 * Returns file extension by MIME-type
 * @param {string} mimeType
 * @return {string|null}
 */
export function getExtensionByMime(mimeType) {
  return mimeToExtension[mimeType] || null;
}

/**
 * Returns MIME-type by file extension
 * @param {string} extension
 * @return {string|null}
 */
export function getMimeByExtension(extension) {
  return extensionToMime[extension] || null;
}
